/**
 * OAuth Service
 * 
 * Implements OAuth 2.0 authorization code flow with PKCE (Proof Key for Code Exchange).
 * Handles authorization requests, token exchange, token refresh, revocation, and introspection.
 * 
 * Client resolution uses the User API's public config endpoint (GET /platforms/{id}/config)
 * instead of direct DynamoDB access, since the browser has no AWS credentials.
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 6.2, 10.2, 10.3, 10.5, REQ-1.2.1
 */

import { authorizationCodesRepository } from '../repositories/authorization-codes.repository';
import { revokedTokensRepository } from '../repositories/revoked-tokens.repository';
import type { User } from '../types/auth.types';
import type {
    AuthorizationCode,
    AuthorizationResult,
    AuthorizeParams,
    OAuthClient,
    TokenExchangeParams,
    TokenIntrospection,
    TokenResponse,
    ValidationResult
} from '../types/oauth.types';
import { generateRandomString } from '../utils/crypto.utils';
import { verifyCodeChallenge } from '../utils/pkce.utils';
import { validateOAuthParams, validateRedirectUri } from '../utils/validation.utils';
import { tokenService } from './token.service';

/**
 * In-memory cache for OAuth clients (reduces repeated HTTP calls)
 * Cache is populated on-demand when clients are requested
 */
let oauthClientsCache = new Map<string, OAuthClient>();

/**
 * Authorization code expiry time in milliseconds (60 seconds)
 */
const AUTH_CODE_EXPIRY_MS = 60 * 1000;

/**
 * User API base URL for fetching platform/OAuth client configuration.
 * The public config endpoint is unauthenticated — no credentials needed.
 */
const getUserApiBaseUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_USER_API_URL) {
    return import.meta.env.VITE_USER_API_URL as string;
  }
  return 'https://dev-api.nodegeeks.org/user';
};

/**
 * Fetch OAuth client config from the User API's public platform config endpoint.
 * 
 * Calls GET /platforms/{clientId}/config which is unauthenticated and returns:
 * platformId, clientId, name, redirectUris, allowedScopes, status, etc.
 * 
 * Maps the User API response to the OAuthClient interface used by the Auth Portal.
 */
async function fetchClientFromUserApi(clientId: string): Promise<OAuthClient | null> {
  const baseUrl = getUserApiBaseUrl();
  const url = `${baseUrl}/platforms/${encodeURIComponent(clientId)}/config`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`OAuth client not found in User API: ${clientId}`);
        return null;
      }
      console.error(`User API returned ${response.status} for client ${clientId}`);
      return null;
    }

    const data = await response.json();

    // Map User API platform config → OAuthClient interface
    const client: OAuthClient = {
      clientId: data.platformId || data.clientId,
      clientName: data.name || data.clientId,
      redirectUris: data.redirectUris || [],
      allowedScopes: data.allowedScopes || ['openid', 'profile', 'email'],
      requireConsent: false,
      trusted: true,
      logoUrl: data.iconUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return client;
  } catch (error) {
    console.error(`Failed to fetch OAuth client from User API for ${clientId}:`, error);
    return null;
  }
}

/**
 * OAuth Service Class
 * 
 * Provides methods for OAuth 2.0 authorization flow including
 * authorization, token exchange, refresh, revocation, and introspection.
 * 
 * Client resolution is done via the User API's public config endpoint,
 * not direct DynamoDB access (which doesn't work from the browser).
 */
export class OAuthService {

  /**
   * Get OAuth client by ID.
   * Checks in-memory cache first, then fetches from User API.
   */
  async getClient(clientId: string): Promise<OAuthClient | undefined> {
    // Check cache first
    const cached = oauthClientsCache.get(clientId);
    if (cached) {
      return cached;
    }

    // Fetch from User API public config endpoint
    const client = await fetchClientFromUserApi(clientId);
    if (client) {
      oauthClientsCache.set(clientId, client);
      return client;
    }

    return undefined;
  }

  /**
   * Get all cached OAuth clients.
   * Note: This only returns clients that have been previously fetched.
   */
  async getAllClients(): Promise<OAuthClient[]> {
    return Array.from(oauthClientsCache.values());
  }

  /**
   * Authorize an OAuth request
   */
  async authorize(params: AuthorizeParams, user: User | null): Promise<AuthorizationResult> {
    const validation = this.validateAuthorizationRequest(params);
    if (!validation.valid) {
      return {
        success: false,
        error: 'invalid_request',
        errorDescription: validation.errors.join(', '),
      };
    }

    if (!user) {
      return {
        success: false,
        error: 'login_required',
        errorDescription: 'User must be authenticated',
      };
    }

    // Validate client via User API
    const client = await this.getClient(params.client_id);

    if (!client) {
      return {
        success: false,
        error: 'invalid_client',
        errorDescription: 'Unknown client',
      };
    }

    if (!validateRedirectUri(params.redirect_uri, client.redirectUris)) {
      return {
        success: false,
        error: 'invalid_request',
        errorDescription: 'Invalid redirect_uri',
      };
    }

    const code = await this.generateAuthorizationCode(user.id, params.client_id, params);

    return {
      success: true,
      code,
      redirectUri: params.redirect_uri,
    };
  }

  /**
   * Validate authorization request parameters
   */
  validateAuthorizationRequest(params: AuthorizeParams): ValidationResult {
    const errors: string[] = [];

    const oauthValidation = validateOAuthParams(params);
    if (!oauthValidation.valid) {
      errors.push(...oauthValidation.errors);
    }

    if (!params.response_type || params.response_type !== 'code') {
      errors.push('response_type must be "code"');
    }

    if (!params.code_challenge) {
      errors.push('code_challenge is required for PKCE');
    }

    if (!params.code_challenge_method) {
      errors.push('code_challenge_method is required for PKCE');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Generate an authorization code
   * In browser environment, calls the OAuth API Lambda
   * In Lambda environment, stores directly in DynamoDB
   */
  async generateAuthorizationCode(
    userId: string,
    clientId: string,
    params: AuthorizeParams
  ): Promise<string> {
    // Check if running in browser (window exists) vs Lambda (no window)
    const isBrowser = typeof window !== 'undefined';
    
    if (isBrowser) {
      // Call OAuth API Lambda to generate the code
      return this.generateAuthorizationCodeViaApi(userId, clientId, params);
    }
    
    // Lambda environment - use DynamoDB directly
    const code = generateRandomString(32);
    const now = Date.now();
    const expiresAt = now + AUTH_CODE_EXPIRY_MS;
    const ttl = Math.floor((expiresAt + 300000) / 1000);

    const authCode: AuthorizationCode = {
      code,
      userId,
      clientId,
      redirectUri: params.redirect_uri,
      codeChallenge: params.code_challenge,
      codeChallengeMethod: params.code_challenge_method,
      scopes: params.scope ? params.scope.split(' ') : [],
      expiresAt,
      used: false,
    };

    try {
      await authorizationCodesRepository.createAuthorizationCode({
        ...authCode,
        ttl,
      });
    } catch (error) {
      console.error('Failed to store authorization code in DynamoDB:', error);
      throw new Error('Failed to generate authorization code');
    }

    return code;
  }

  /**
   * Generate authorization code by calling the OAuth API Lambda
   * Used when running in browser environment
   */
  private async generateAuthorizationCodeViaApi(
    userId: string,
    clientId: string,
    params: AuthorizeParams
  ): Promise<string> {
    // Use the API Gateway URL directly (not CloudFront) for the authorize endpoint
    // This avoids the CloudFront → SPA routing conflict
    // The API Gateway URL is injected at build time or falls back to CloudFront + /oauth path
    const oauthApiGatewayUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OAUTH_API_GATEWAY_URL)
      || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OAUTH_API_URL)
      || 'https://dev-auth.nodegeeks.org';
    
    // Build the authorization URL with all required parameters
    const url = new URL(`${oauthApiGatewayUrl}/oauth/authorize`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', params.redirect_uri);
    url.searchParams.set('code_challenge', params.code_challenge);
    url.searchParams.set('code_challenge_method', params.code_challenge_method);
    if (params.scope) url.searchParams.set('scope', params.scope);
    if (params.state) url.searchParams.set('state', params.state);

    // Include the tenant the user selected in the company selector so the issued
    // token is scoped to it. Stored in sessionStorage by the AuthContext when a
    // tenant is selected/auto-selected. The token endpoint validates membership.
    try {
      const storedTenant = sessionStorage.getItem('activeTenant');
      if (storedTenant) {
        const activeTenant = JSON.parse(storedTenant) as { tenantId?: string };
        if (activeTenant?.tenantId) {
          url.searchParams.set('tenant_id', activeTenant.tenantId);
        }
      }
    } catch {
      // Non-fatal: fall back to the token endpoint's default membership resolution.
    }

    try {
      // Call the OAuth API - it will detect CORS request and return JSON
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-User-Id': userId, // Pass user ID to the Lambda
        },
      });
      
      // Parse JSON response (Lambda returns JSON for CORS requests)
      if (response.ok) {
        const data = await response.json();
        if (data.code) {
          return data.code;
        }
        // Check for error in response
        if (data.error) {
          throw new Error(data.error_description || data.error);
        }
      }
      
      // Handle non-OK responses
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error_description || errorData.error || `HTTP ${response.status}`);
    } catch (error) {
      console.error('Failed to generate authorization code via API:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to generate authorization code');
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeCodeForTokens(
    params: TokenExchangeParams,
    user: User
  ): Promise<TokenResponse | { error: string; error_description: string }> {
    if (params.grant_type !== 'authorization_code') {
      return {
        error: 'unsupported_grant_type',
        error_description: 'Only authorization_code grant type is supported',
      };
    }

    if (!params.code || !params.code_verifier || !params.client_id) {
      return {
        error: 'invalid_request',
        error_description: 'Missing required parameters',
      };
    }

    let authCode: AuthorizationCode | null;
    try {
      authCode = await authorizationCodesRepository.getAuthorizationCode(params.code);
    } catch (error) {
      console.error('Failed to retrieve authorization code from DynamoDB:', error);
      return {
        error: 'server_error',
        error_description: 'Failed to validate authorization code',
      };
    }

    if (!authCode) {
      return {
        error: 'invalid_grant',
        error_description: 'Invalid or expired authorization code',
      };
    }

    if (authCode.used) {
      await authorizationCodesRepository.deleteAuthorizationCode(params.code);
      return {
        error: 'invalid_grant',
        error_description: 'Authorization code has already been used',
      };
    }

    if (Date.now() > authCode.expiresAt) {
      await authorizationCodesRepository.deleteAuthorizationCode(params.code);
      return {
        error: 'invalid_grant',
        error_description: 'Authorization code has expired',
      };
    }

    if (authCode.clientId !== params.client_id) {
      return {
        error: 'invalid_grant',
        error_description: 'Client ID mismatch',
      };
    }

    const pkceValid = await this.verifyPKCEChallenge(
      params.code_verifier,
      authCode.codeChallenge,
      authCode.codeChallengeMethod
    );

    if (!pkceValid) {
      await authorizationCodesRepository.deleteAuthorizationCode(params.code);
      return {
        error: 'invalid_grant',
        error_description: 'PKCE verification failed',
      };
    }

    try {
      await authorizationCodesRepository.markCodeAsUsed(params.code);
    } catch (error) {
      console.error('Failed to mark authorization code as used:', error);
    }

    const accessToken = tokenService.generateAccessToken(
      user.id,
      user.email,
      user.displayName,
      user.roles,
      [],
      params.client_id
    );

    const refreshToken = tokenService.generateRefreshToken(user.id, params.client_id);
    const idToken = tokenService.generateIdToken(user, params.client_id);

    try {
      await authorizationCodesRepository.deleteAuthorizationCode(params.code);
    } catch (error) {
      console.error('Failed to delete used authorization code:', error);
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken,
      id_token: idToken,
      token_type: 'Bearer',
      expires_in: 3600,
    };
  }

  /**
   * Refresh access token with token rotation
   */
  async refreshAccessToken(
    refreshToken: string,
    clientId: string,
    user: User
  ): Promise<TokenResponse | { error: string; error_description: string }> {
    try {
      const isRevoked = await revokedTokensRepository.isTokenRevoked(refreshToken);
      if (isRevoked) {
        return {
          error: 'invalid_grant',
          error_description: 'Refresh token has been revoked',
        };
      }
    } catch (error) {
      console.error('Failed to check token revocation status:', error);
    }

    const validation = tokenService.validateRefreshToken(refreshToken);
    if (!validation.valid) {
      return {
        error: 'invalid_grant',
        error_description: validation.error || 'Invalid refresh token',
      };
    }

    const payload = validation.payload as { clientId?: string };
    if (payload.clientId !== clientId) {
      return {
        error: 'invalid_grant',
        error_description: 'Client ID mismatch',
      };
    }

    try {
      const ttl = Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60);
      await revokedTokensRepository.revokeToken({
        token: refreshToken,
        tokenType: 'refresh',
        userId: user.id,
        revokedAt: new Date().toISOString(),
        ttl,
        reason: 'token_rotation',
      });
    } catch (error) {
      console.error('Failed to revoke old refresh token:', error);
    }

    const newAccessToken = tokenService.generateAccessToken(
      user.id,
      user.email,
      user.displayName,
      user.roles,
      [],
      clientId
    );

    const newRefreshToken = tokenService.generateRefreshToken(user.id, clientId);
    const idToken = tokenService.generateIdToken(user, clientId);

    return {
      access_token: newAccessToken,
      refresh_token: newRefreshToken,
      id_token: idToken,
      token_type: 'Bearer',
      expires_in: 3600,
    };
  }

  /**
   * Revoke a token
   */
  async revokeToken(token: string, userId?: string): Promise<void> {
    try {
      const ttl = Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60);
      await revokedTokensRepository.revokeToken({
        token,
        tokenType: 'access',
        userId: userId || 'unknown',
        revokedAt: new Date().toISOString(),
        ttl,
        reason: 'user_revocation',
      });
    } catch (error) {
      console.error('Failed to revoke token in DynamoDB:', error);
      throw new Error('Failed to revoke token');
    }
  }

  /**
   * Introspect a token
   */
  async introspectToken(token: string): Promise<TokenIntrospection> {
    try {
      const isRevoked = await revokedTokensRepository.isTokenRevoked(token);
      if (isRevoked) {
        return { active: false };
      }
    } catch (error) {
      console.error('Failed to check token revocation status:', error);
    }

    const validation = tokenService.validateAccessToken(token);
    if (!validation.valid) {
      return { active: false };
    }

    const payload = validation.payload as {
      permissions?: string[];
      aud?: string;
      email?: string;
      exp?: number;
      iat?: number;
      sub?: string;
    };

    return {
      active: true,
      scope: payload.permissions?.join(' '),
      client_id: payload.aud,
      username: payload.email,
      token_type: 'Bearer',
      exp: payload.exp,
      iat: payload.iat,
      sub: payload.sub,
    };
  }

  /**
   * Verify PKCE challenge
   */
  async verifyPKCEChallenge(
    codeVerifier: string,
    codeChallenge: string,
    method: 'S256' | 'plain'
  ): Promise<boolean> {
    return verifyCodeChallenge(codeVerifier, codeChallenge, method);
  }

  /**
   * Clear the client cache (useful for testing or forced refresh)
   */
  clearClientCache(): void {
    oauthClientsCache.clear();
  }
}

// Export singleton instance
export const oauthService = new OAuthService();
